import { Link } from 'react-router-dom'
import { ExternalLink, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useConfirm } from '@/components/ui/confirm-provider'
import { Skeleton } from '@/components/ui/skeleton'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import {
  useDiscardSeedProject,
  usePublishSeedProject,
  useSeedProjects,
} from '@/features/admin/use-seed-review'

/**
 * Phase 19 admin surface — list AI-generated PRIVATE drafts and decide per-row:
 * Publish (flip to PUBLIC) / Discard (delete). Authenticated route; the backend's
 * AdminAccessPolicy rejects non-admin users with 403, which surfaces as the
 * "you don't have access" alert below.
 */
export const AdminSeedReviewPage = () => {
  const { data, isLoading, isError, error, refetch } = useSeedProjects()
  const publish = usePublishSeedProject()
  const discard = useDiscardSeedProject()
  const confirm = useConfirm()

  const status = (error as { status?: number } | undefined)?.status
  const isAccessDenied = status === 403 || status === 401

  const onPublish = async (id: string, title: string) => {
    try {
      await publish.mutateAsync(id)
      toast.success(`Published "${title}"`)
    } catch {
      toast.error('Failed to publish')
    }
  }
  const onDiscard = async (id: string, title: string) => {
    const ok = await confirm({
      title: `Discard "${title}"?`,
      description: 'This permanently deletes the project. Cannot be undone.',
      confirmText: 'Discard',
      destructive: true,
    })
    if (!ok) return
    try {
      await discard.mutateAsync(id)
      toast.success(`Discarded "${title}"`)
    } catch {
      toast.error('Failed to discard')
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Sparkles className="h-5 w-5 text-amber-600" /> Seed review
        </h1>
        <p className="text-sm text-muted-foreground">
          AI-generated drafts pending review. Publish to flip a draft to PUBLIC, or discard to
          delete it entirely.
        </p>
      </div>

      {isAccessDenied && (
        <Alert variant="destructive">
          <AlertTitle>You don&apos;t have access</AlertTitle>
          <AlertDescription>
            Seed review is restricted to handles configured in <code>todone.admin.handles</code>.
            Ask whoever runs the deployment to add you.
          </AlertDescription>
        </Alert>
      )}

      {!isAccessDenied && isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load seed projects</AlertTitle>
          <AlertDescription>
            <button
              type="button"
              onClick={() => refetch()}
              className="underline underline-offset-2"
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-md border bg-background p-8 text-center">
          <p className="text-base font-medium">No drafts pending review.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Run the seed generator first: <code className="text-xs">make seed-launch-content</code>
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="space-y-3">
          {data.map((p) => (
            <Card key={p.id}>
              <CardContent className="flex gap-4 p-4">
                {p.heroThumbUrl && (
                  <img
                    src={p.heroThumbUrl}
                    alt=""
                    className="h-20 w-20 flex-shrink-0 rounded object-cover"
                  />
                )}
                <div className="flex-1 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-base font-semibold">{p.title}</h3>
                    <Link
                      to={canonicalProjectUrl(p.author.handle, p.slug)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      <ExternalLink className="inline h-3 w-3" /> Preview
                    </Link>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    @{p.author.handle} · {new Date(p.createdAt).toLocaleDateString()}
                  </div>
                  {p.description && (
                    <p className="line-clamp-2 text-sm text-muted-foreground">{p.description}</p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() => onPublish(p.id, p.title)}
                    disabled={publish.isPending}
                  >
                    Publish
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDiscard(p.id, p.title)}
                    disabled={discard.isPending}
                    className="gap-1"
                  >
                    <Trash2 className="h-3 w-3" /> Discard
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
